import { useClientSelection } from "../client-context";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle, ArrowRight, ArrowUp, Check, ClipboardCheck, PanelLeft, RotateCw, Search, ShieldCheck, SquarePen, X,
} from "lucide-react";
import { AGENCY_TZ, api, formatDate } from "../api";
import { useApi } from "../hooks";
import { local } from "../storage";
import { ErrorState } from "../components/Ui";
import ChatMessage, { AnalystMark, type ChatMessageData } from "../components/ChatMessage";
import PromptPicker, { type LibraryPrompt } from "../components/PromptPicker";
import { noDash } from "../insights";
import "./Analyst.css";

type Session = {
  id: number;
  session_name: string | null;
  client_id: number;
  client_name: string;
  last_message_at: string | null;
  created_at: string | null;
  /** The chat's first question (the server lists only chats with a message). */
  first_message?: string | null;
};
type ChatResponse = { session_id: number; response: string; proposed_action: { id: number; status?: string } | null };
// Old chats were named after the day they were opened ("Session 9/15/2026"), which
// only repeated the date beside them: those read as unnamed.
const DATED_NAME = /^Session \d{1,2}\/\d{1,2}\/\d{4}$/;
/** What a proposal card says, by the proposal's state in Approvals. */
const PROPOSAL_STATE: Record<string, string> = {
  pending: "needs a decision",
  approved: "was approved",
  auto_approved: "was approved",
  executed: "was approved and carried out",
  rejected: "was turned down",
  rolled_back: "was rolled back",
};
type SendFailure = { message: string; text: string; prompt: LibraryPrompt | undefined };

const MAX_LENGTH = 4000;
const COUNTER_FROM = 3500;
const STARTERS = [
  "How did spend and calls trend this month?",
  "What's hurting the booking rate?",
  "Which campaigns should we cut or scale?",
  "Summarize the last call center report.",
];

// Chats started from the composer have no name on the server, so this browser
// remembers each one's first question as its title. A convenience only: a
// blocked or cleared store falls back to "Untitled chat".
const TITLES_KEY = "nl-aos-analyst-titles";
function readTitles(): Record<string, string> {
  try { return JSON.parse(local.get(TITLES_KEY) || "{}") || {}; } catch { return {}; }
}
function excerpt(text: string) {
  const line = text.replace(/\s+/g, " ").trim();
  return line.length > 64 ? `${line.slice(0, 62).trimEnd()}…` : line;
}

const DAY = 86_400_000;
function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}
function activity(s: Session) {
  const t = new Date(s.last_message_at || s.created_at || "").getTime();
  return Number.isNaN(t) ? 0 : t;
}
function relativeTime(s: Session) {
  const t = activity(s);
  if (!t) return "";
  const diff = Date.now() - t;
  const today = startOfToday();
  if (diff < 60_000) return "Just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (t >= today) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (t >= today - DAY) return "Yesterday";
  if (t >= today - 6 * DAY) return new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: AGENCY_TZ }).format(t);
  return formatDate(s.last_message_at || s.created_at);
}

export default function Analyst() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selection = useClientSelection();
  const sessions = useApi<Session[]>("/api/analyst/sessions");
  const prompts = useApi<LibraryPrompt[]>("/api/prompts");

  const sessionId = Number(params.get("session")) || null;
  const session = sessions.data?.find((s) => s.id === sessionId);
  // A chat opened by link for another client brings the sidebar to that client,
  // so the page and the client name above it agree.
  useEffect(() => {
    if (session && selection.clientId && session.client_id !== selection.clientId) {
      const next = new URLSearchParams(params);
      next.set("client_id", String(session.client_id));
      setParams(next, { replace: true });
    }
  }, [session?.id, session?.client_id, selection.clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  // ?prompt_id= comes from "Use in AI Analyst" in the Prompt Library.
  const promptId = params.get("prompt_id");
  const prompt = prompts.data?.find((p) => String(p.id) === promptId);
  const setPrompt = (p: LibraryPrompt | null) => {
    const next = new URLSearchParams(params);
    if (p) next.set("prompt_id", String(p.id));
    else next.delete("prompt_id");
    setParams(next, { replace: true });
  };

  const [titles, setTitles] = useState(readTitles);
  const rememberTitle = (id: number, title: string) => {
    if (!title.trim()) return;
    setTitles((current) => {
      if (current[id]) return current;
      const next = { ...current, [id]: excerpt(title) };
      local.set(TITLES_KEY, JSON.stringify(next));
      return next;
    });
  };
  const titleOf = (s: Session) => {
    const named = s.session_name?.trim();
    return (named && !DATED_NAME.test(named) ? named : "") || titles[s.id] || (s.first_message ? excerpt(s.first_message) : "") || "Untitled chat";
  };

  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [history, setHistory] = useState<{ loading: boolean; error: string | null }>({ loading: false, error: null });
  const [historyRevision, setHistoryRevision] = useState(0);
  const [draft, setDraft] = useState<string>(() => (typeof location.state?.analystDraft === "string" ? location.state.analystDraft : ""));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<SendFailure | null>(null);
  const [proposal, setProposal] = useState<{ id: number; after: ChatMessageData["id"] } | null>(null);
  const [task, setTask] = useState<{ busy?: boolean; id?: number; error?: string }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Bumped whenever another conversation opens, so a reply that lands after
  // the user moved on is never appended to the wrong thread.
  const thread = useRef(0);
  // The session a new chat just created: its messages are already on screen.
  const adopted = useRef<number | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const drawerToggle = useRef<HTMLButtonElement>(null);
  const nearBottom = useRef(true);
  const scrollIntent = useRef<"bottom" | "turn" | null>(null);

  useEffect(() => {
    if (sessionId !== null && adopted.current === sessionId) return;
    adopted.current = null;
    thread.current += 1;
    setBusy(false);
    setFailure(null);
    setProposal(null);
    setTask({});
    setMessages([]);
    if (!sessionId) { setHistory({ loading: false, error: null }); return; }
    const controller = new AbortController();
    setHistory({ loading: true, error: null });
    api<ChatMessageData[]>(`/api/analyst/sessions/${sessionId}/messages`, { signal: controller.signal })
      .then((rows) => {
        setMessages(rows);
        setHistory({ loading: false, error: null });
        scrollIntent.current = "bottom";
        const first = rows.find((m) => m.role === "user" && m.content_text);
        if (first) rememberTitle(sessionId, first.content_text);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setHistory({ loading: false, error: reason instanceof Error ? reason.message : "Could not load this conversation" });
      });
    return () => controller.abort();
  }, [sessionId, historyRevision]);

  // Follow new messages only when the reader is already at the bottom, or has
  // just sent. A long reply lands with the start of the turn in view, not its end.
  useLayoutEffect(() => {
    const el = scroller.current;
    const intent = scrollIntent.current;
    scrollIntent.current = null;
    if (!el || (!intent && !nearBottom.current)) return;
    let top = el.scrollHeight - el.clientHeight;
    if (intent === "turn") {
      const turns = el.querySelectorAll<HTMLElement>(".ac-msg-user");
      const turn = turns[turns.length - 1];
      if (turn) top = Math.min(top, turn.offsetTop - 16);
    }
    el.scrollTop = Math.max(0, top);
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
  }, [messages, busy, failure, proposal]);

  // Composer grows with its text; CSS caps it at about eight lines.
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  useEffect(() => {
    if (!drawerOpen) return;
    document.querySelector<HTMLElement>(".ac-rail")?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeDrawer(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  function closeDrawer() {
    setDrawerOpen(false);
    drawerToggle.current?.focus();
  }
  const desktop = () => window.matchMedia("(min-width: 761px)").matches;

  function openConversation(id: number) {
    if (id !== sessionId) setDraft("");
    setDrawerOpen(false);
    const next = new URLSearchParams(params);
    next.set("session", String(id));
    setParams(next);
  }
  function newChat() {
    setDrawerOpen(false);
    if (sessionId) {
      setDraft("");
      const next = new URLSearchParams(params);
      next.delete("session");
      setParams(next);
    }
    if (desktop()) requestAnimationFrame(() => input.current?.focus());
  }

  const newChatClientId = sessionId ? null : selection.clientId;
  const clientName = session?.client_name ?? selection.client?.name ?? "";
  const notFound = Boolean(sessionId && sessions.data && !session && !history.loading && !messages.length && !busy);
  const canCompose = !history.loading && !notFound && (sessionId ? true : Boolean(newChatClientId));

  async function send(typed: string, withPrompt: LibraryPrompt | undefined) {
    if (busy || !canCompose) return;
    // A prompt can run on its own; the analyst still needs a message to send.
    const text = typed.trim() || (withPrompt ? `Run this prompt for ${clientName || "this client"}.` : "");
    if (!text) return;
    const token = thread.current;
    const creating = !sessionId;
    const localId = `local-${Date.now()}`;
    setMessages((m) => [...m, { id: localId, role: "user", content_text: text, prompt_name: withPrompt?.prompt_name, created_at: new Date().toISOString() }]);
    setDraft("");
    setFailure(null);
    setProposal(null);
    setBusy(true);
    scrollIntent.current = "bottom";
    try {
      const r = await api<ChatResponse>("/api/analyst/chat", {
        method: "POST",
        body: JSON.stringify({
          ...(creating ? { client_id: newChatClientId } : { session_id: sessionId }),
          message: text,
          prompt_library_id: withPrompt?.id,
        }),
      });
      if (token !== thread.current) { sessions.reload(); return; }
      const replyId = `local-${Date.now()}-reply`;
      setMessages((m) => [...m, { id: replyId, role: "assistant", content_text: r.response, created_at: new Date().toISOString() }]);
      setProposal(r.proposed_action?.id ? { id: r.proposed_action.id, after: replyId } : null);
      scrollIntent.current = "turn";
      // A prompt applies to one message; follow-ups are plain conversation.
      const next = new URLSearchParams(window.location.search);
      next.delete("prompt_id");
      if (creating) {
        adopted.current = r.session_id;
        next.set("session", String(r.session_id));
        rememberTitle(r.session_id, typed.trim() || withPrompt?.prompt_name || "");
      }
      if (location.state?.analystDraft) navigate({ search: `?${next}` }, { replace: true, state: null });
      else if (next.toString() !== new URLSearchParams(window.location.search).toString()) setParams(next, { replace: true });
      sessions.reload();
    } catch (e) {
      if (token !== thread.current) return;
      setMessages((m) => m.filter((x) => x.id !== localId));
      setDraft((current) => current || typed);
      setFailure({ message: e instanceof Error ? e.message : "The analyst could not answer.", text: typed, prompt: withPrompt });
    } finally {
      if (token === thread.current) {
        setBusy(false);
        requestAnimationFrame(() => input.current?.focus());
      }
    }
  }

  async function createTask() {
    if (!session) return;
    setTask({ busy: true });
    try {
      const last = messages.filter((m) => m.role === "assistant").at(-1);
      const r = await api<{ id: number }>("/api/tasks", {
        method: "POST",
        body: JSON.stringify({
          client_id: session.client_id,
          title: `Review analyst recommendation for ${session.client_name}`,
          body: last?.content_text || "Review this analyst session.",
          deep_link: `/analyst?client_id=${session.client_id}&session=${session.id}`,
        }),
      });
      setTask({ id: r.id });
    } catch (e) {
      setTask({ error: e instanceof Error ? e.message : "Could not create task" });
    }
  }

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const today = startOfToday();
    const buckets: { label: string; items: Session[] }[] = [
      { label: "Today", items: [] },
      { label: "This week", items: [] },
      { label: "Older", items: [] },
    ];
    for (const s of sessions.data || []) {
      // A client's page lists that client's chats only (every chat is still
      // fetched, so a link to another client's chat still opens).
      if (selection.clientId && s.client_id !== selection.clientId) continue;
      if (q && !`${titleOf(s)} ${s.client_name}`.toLowerCase().includes(q)) continue;
      const t = activity(s);
      buckets[t >= today ? 0 : t >= today - 6 * DAY ? 1 : 2].items.push(s);
    }
    for (const b of buckets) b.items.sort((a, b2) => activity(b2) - activity(a));
    return buckets.filter((b) => b.items.length);
  }, [sessions.data, query, titles, selection.clientId]);

  const lastAssistant = messages.map((m) => m.role).lastIndexOf("assistant");
  const hasReply = lastAssistant >= 0;
  const showWelcome = !sessionId && !messages.length && !busy;
  const heading = session ? titleOf(session) : sessionId ? titles[sessionId] || "Conversation" : "New chat";
  const canSend = canCompose && !busy && Boolean(draft.trim() || prompt);

  const rail = (
    <aside className="ac-rail" id="analyst-conversations" aria-label="Conversations" tabIndex={-1}>
      <div className="ac-rail-head">
        <div className="ac-rail-top">
          <button type="button" className="ac-new" onClick={newChat}>
            <SquarePen aria-hidden="true" />New chat
          </button>
          <button type="button" className="ac-icon-btn ac-drawer-only" onClick={closeDrawer} aria-label="Close conversations">
            <X aria-hidden="true" />
          </button>
        </div>
        <label className="ac-search">
          <Search aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search conversations" aria-label="Search conversations" type="search" />
        </label>
      </div>
      <nav className="ac-rail-list" aria-label="Conversation history">
        {sessions.loading && !sessions.data ? (
          <div className="ac-rail-skeleton" aria-label="Loading conversations">{[0, 1, 2, 3].map((i) => <span key={i} />)}</div>
        ) : sessions.error && !sessions.data ? (
          <p className="ac-rail-empty">Conversations could not load. <button type="button" className="ac-link-btn" onClick={sessions.reload}>Retry</button></p>
        ) : !groups.length ? (
          <p className="ac-rail-empty">{query ? `No conversations match “${query.trim()}”.` : "No conversations yet. Your chats will appear here."}</p>
        ) : groups.map((g) => (
          <section className="ac-group" key={g.label}>
            <h3 className="ac-group-label">{g.label}</h3>
            <ul>
              {g.items.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={s.id === sessionId ? "ac-row active" : "ac-row"}
                    aria-current={s.id === sessionId ? "page" : undefined}
                    onClick={() => openConversation(s.id)}
                  >
                    <strong>{titleOf(s)}</strong>
                    <span><em>{s.client_name}</em><time>{relativeTime(s)}</time></span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
    </aside>
  );

  return (
    <div className={drawerOpen ? "analyst-chat drawer-open" : "analyst-chat"}>
      <h1 className="sr-only">AI analyst</h1>
      {rail}
      {drawerOpen && <button type="button" className="ac-scrim" aria-label="Close conversations" tabIndex={-1} onClick={closeDrawer} />}

      <section className="ac-main" aria-label={heading}>
        <header className="ac-head">
          <button
            ref={drawerToggle}
            type="button"
            className="ac-icon-btn ac-drawer-only"
            onClick={() => setDrawerOpen(true)}
            aria-label="Show conversations"
            aria-expanded={drawerOpen}
            aria-controls="analyst-conversations"
          >
            <PanelLeft aria-hidden="true" />
          </button>
          <div className="ac-head-title">
            <h2 title={heading}>{heading}</h2>
            <p>{clientName || "No client selected"}</p>
          </div>
          <div className="ac-head-actions">
            {task.id ? (
              <span className="ac-task-done" role="status">
                <Check aria-hidden="true" />Task created. <Link to={`/tasks?task=${task.id}`}>Open task</Link>
              </span>
            ) : (
              <>
                {task.error && <span className="ac-task-error" role="alert">{task.error}</span>}
                <button
                  type="button"
                  className="ac-head-btn"
                  onClick={createTask}
                  disabled={!session || !hasReply || task.busy || busy}
                  aria-label="Create review task"
                  title="Create a task to review the latest recommendation"
                >
                  <ClipboardCheck aria-hidden="true" />
                  <span className="ac-label">{task.busy ? "Creating…" : "Create review task"}</span>
                </button>
              </>
            )}
            <button type="button" className="ac-icon-btn ac-drawer-only" onClick={newChat} aria-label="New chat">
              <SquarePen aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="ac-scroll" ref={scroller} onScroll={(e) => {
          const el = e.currentTarget;
          nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
        }}>
          {history.loading ? (
            <div className="ac-thread ac-thread-skeleton" aria-label="Loading conversation">
              <span className="ac-sk-user" /><span className="ac-sk-line" /><span className="ac-sk-line" /><span className="ac-sk-line short" />
            </div>
          ) : history.error ? (
            <div className="ac-thread"><ErrorState message={history.error} onRetry={() => setHistoryRevision((v) => v + 1)} /></div>
          ) : showWelcome ? (
            <div className="ac-welcome">
              <AnalystMark />
              {newChatClientId ? (
                <>
                  <h2>Ask anything about {clientName}</h2>
                  <p>
                    {prompt
                      ? `“${noDash(prompt.prompt_name)}” is ready. Add any details it needs, or press send to run it as is.`
                      : "The analyst reads this client’s connected data. Check sources and freshness before acting on a recommendation."}
                  </p>
                </>
              ) : (
                <>
                  <h2>Pick a client to start</h2>
                  <p>Each conversation belongs to one client, so the analyst knows which data to read.</p>
                </>
              )}
              {(selection.clients.data?.length || 0) > 0 && !newChatClientId && (
                <label className="ac-client">
                  <span>Client</span>
                  <select value={newChatClientId ?? ""} onChange={(e) => e.target.value && selection.setClientId(Number(e.target.value))}>
                    {!newChatClientId && <option value="">Choose client</option>}
                    {selection.clients.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
              )}
              {newChatClientId && (
                <div className="ac-starters">
                  {STARTERS.map((s) => (
                    <button key={s} type="button" className="ac-starter" onClick={() => send(s, prompt)}>
                      <span>{s}</span><ArrowRight aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : notFound ? (
            <div className="ac-welcome">
              <h2>Conversation not found</h2>
              <p>It may have been archived, or it belongs to someone else.</p>
              <button type="button" className="ac-new ac-new-inline" onClick={newChat}><SquarePen aria-hidden="true" />Start a new chat</button>
            </div>
          ) : (
            <div className="ac-thread" role="log" aria-label="Conversation with the analyst" aria-busy={busy}>
              {!messages.length && !busy && <p className="ac-thread-empty">No messages yet. Ask the analyst something to begin.</p>}
              {messages.map((m, i) => {
                // The reply that proposed an action carries it: from this send, or from the saved chat.
                const card = proposal?.after === m.id ? { id: proposal.id, status: "pending" } : m.proposal_id ? { id: m.proposal_id, status: m.proposal_status || "pending" } : null;
                return (
                  <ChatMessage key={m.id} message={m} last={i === lastAssistant && !busy}>
                    {card && (
                      <div className="ac-proposal">
                        <ShieldCheck aria-hidden="true" />
                        <span>Proposed action #{card.id} {PROPOSAL_STATE[card.status] || "needs a decision"}</span>
                        <Link to={`/approvals?record=${card.id}`}>{card.status === "pending" ? "Review in Approvals" : "Open in Approvals"}<ArrowRight aria-hidden="true" /></Link>
                      </div>
                    )}
                  </ChatMessage>
                );
              })}
              {busy && (
                <div className="ac-msg ac-msg-assistant ac-thinking" aria-live="polite">
                  <AnalystMark />
                  <div className="ac-dots"><i /><i /><i /><span>Thinking</span></div>
                </div>
              )}
              {failure && (
                <div className="ac-error" role="alert">
                  <AlertCircle aria-hidden="true" />
                  <p><strong>The message was not sent.</strong> {failure.message}</p>
                  <button type="button" className="ac-retry" onClick={() => send(failure.text, failure.prompt)}>
                    <RotateCw aria-hidden="true" />Retry
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ac-composer-wrap">
          <form className="ac-composer" onSubmit={(e) => { e.preventDefault(); void send(draft, prompt); }}>
            <div className="ac-picker">
              <PromptPicker prompts={prompts} selected={prompt} onSelect={setPrompt} disabled={busy} />
            </div>
            <label htmlFor="analyst-message" className="sr-only">Message the analyst</label>
            <textarea
              id="analyst-message"
              ref={input}
              className="ac-input"
              rows={1}
              value={draft}
              maxLength={MAX_LENGTH}
              disabled={!canCompose && !history.loading}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void send(draft, prompt);
                }
              }}
              placeholder={
                prompt
                  ? "Add the details this prompt needs, or send it as is…"
                  : !canCompose && !sessionId
                    ? "Pick a client to start a conversation"
                    : clientName
                      ? `Ask about ${clientName}…`
                      : "Message the analyst…"
              }
            />
            <span className="ac-meta" aria-live="polite">
              {draft.length > COUNTER_FROM && (
                <span className={draft.length >= MAX_LENGTH ? "ac-count full" : "ac-count"}>
                  {draft.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
                </span>
              )}
            </span>
            <button type="submit" className="ac-send" disabled={!canSend} aria-label={busy ? "Sending" : prompt ? "Run prompt" : "Send message"}>
              <ArrowUp aria-hidden="true" />
            </button>
          </form>
          <p className="ac-hint"><span className="ac-hint-keys">Enter to send, Shift+Enter for a new line</span></p>
        </div>
      </section>
    </div>
  );
}
